import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import Calendar from './Calendar';
import { AlertCircle } from 'lucide-react';
import { formatDateLocal } from '../utils/dateUtils';
import {
  CalendarDayAvailability,
  CalendarDayStatus,
} from '../utils/productCalendarAvailability';

interface ProductAvailabilityCalendarProps {
  productId: string;
  requestedQuantity?: number;
  onSelectDates?: (startDate: Date | null, endDate: Date | null) => void;
}

const ProductAvailabilityCalendar: React.FC<ProductAvailabilityCalendarProps> = ({
  productId,
  requestedQuantity = 1,
  onSelectDates,
}) => {
  const [dayAvailability, setDayAvailability] = useState<Record<string, CalendarDayStatus>>({});
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailability = useCallback(
    async (month: Date, quantity: number) => {
      setLoading(true);
      setError(null);

      try {
        const startDate = new Date(month.getFullYear(), month.getMonth(), 1);
        const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);

        const { data, error: rpcError } = await supabase.rpc('get_product_calendar_availability', {
          p_product_id: productId,
          p_start_date: formatDateLocal(startDate),
          p_end_date: formatDateLocal(endDate),
          p_quantity: quantity,
        });

        if (rpcError) throw rpcError;

        const availabilityMap: Record<string, CalendarDayStatus> = {};
        (data as CalendarDayAvailability[] | null)?.forEach((row) => {
          const dateKey =
            typeof row.date === 'string' ? row.date.slice(0, 10) : formatDateLocal(new Date(row.date));
          availabilityMap[dateKey] = row.status;
        });

        setDayAvailability(availabilityMap);
      } catch (err: unknown) {
        console.error('Error fetching calendar availability:', err);
        setError(err instanceof Error ? err.message : 'Okänt fel');
      } finally {
        setLoading(false);
      }
    },
    [productId]
  );

  useEffect(() => {
    fetchAvailability(visibleMonth, requestedQuantity);
  }, [fetchAvailability, visibleMonth, requestedQuantity]);

  const handleMonthChange = useCallback((month: Date) => {
    const normalized = new Date(month.getFullYear(), month.getMonth(), 1);
    setVisibleMonth((prev) => {
      if (
        prev.getFullYear() === normalized.getFullYear() &&
        prev.getMonth() === normalized.getMonth()
      ) {
        return prev;
      }
      return normalized;
    });
  }, []);

  const handleSelectDates = (startDate: Date | null, endDate: Date | null) => {
    onSelectDates?.(startDate, endDate);
  };

  if (loading && Object.keys(dayAvailability).length === 0) {
    return (
      <div className="animate-pulse">
        <div className="h-64 bg-gray-200 rounded-none"></div>
      </div>
    );
  }

  if (error && Object.keys(dayAvailability).length === 0) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-none">
        <div className="flex">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>Ett fel uppstod: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={loading ? 'opacity-70 transition-opacity' : undefined}>
      <Calendar
        dayAvailability={dayAvailability}
        onSelectDates={handleSelectDates}
        onMonthChange={handleMonthChange}
      />
    </div>
  );
};

export default ProductAvailabilityCalendar;
